import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Trash2, ExternalLink } from "react-feather";
import toast from "react-hot-toast";
import { confirmDelete } from "../common/ConfirmDialog";
import { MAROON } from "../common/brandColors";
import {
  useListNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationReadMutation,
  useClearNotificationsMutation,
} from "../../features/notifications/notificationsApi";
import { detailPathFor } from "../../features/notifications/notificationRoutes";

/**
 * The Flutter app's notification bell, brought to the web header (2026-09-09).
 *
 * Mobile has a floating bell in the top-right of every screen that opens a
 * Notification Centre route; the web equivalent of that route is a dropdown off
 * the same bell, since a whole page for a list you act on and dismiss would be a
 * navigation away from the work the notification is about.
 *
 * Behaviour is deliberately identical to the mobile screen, because both clients
 * read one inbox:
 *  - unread only. A read notification drops off the list - the server keeps the
 *    row, but only the unread ones are worth surfacing.
 *  - clicking marks read and then deep-links to the voucher / LPO / enquiry /
 *    work item it is about.
 *  - "Clear all" wipes every row, read or unread, and is confirmed first.
 *
 * The count polls every 60s, matching the Flutter bell's own poll. There is no
 * push-driven invalidation on the web: that would need a service worker and a
 * Firebase web registration this app does not have, so polling is the whole
 * mechanism rather than a fallback.
 */
const POLL_MS = 60000;

const relativeTime = (value) => {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const { data: unreadCount = 0 } = useGetUnreadNotificationCountQuery(undefined, {
    pollingInterval: POLL_MS,
  });
  // The list itself is only fetched while the panel is open - the bell is on
  // every screen, and there is no reason to carry 30 rows around to render a
  // number that has its own endpoint.
  const { data: items = [], isFetching } = useListNotificationsQuery(
    { page: 1, pageSize: 30 },
    { skip: !open }
  );
  const [markRead] = useMarkNotificationReadMutation();
  const [clearAll, { isLoading: isClearing }] = useClearNotificationsMutation();

  // Click-outside and Escape, rather than bootstrap's own dropdown: this panel
  // owns real state (a fetch, a confirm dialog) and has to stay open across a
  // re-render, which the data-bs-toggle version in the header does not.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const unread = items.filter((item) => !item.isRead);

  const handleOpenItem = async (item) => {
    const path = detailPathFor(item);
    try {
      if (!item.isRead) await markRead(item.recordGuid).unwrap();
    } catch (e) {
      // Marking read failing must not swallow the navigation - the user asked to
      // see the thing, and the row will still be there to re-read.
      toast.error(e?.message || "Could not mark this as read");
    }
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  const handleClearAll = async () => {
    const confirmed = await confirmDelete({
      title: "Clear all notifications?",
      text: "This removes every notification, read or unread. This cannot be undone.",
    });
    if (!confirmed) return;
    try {
      await clearAll().unwrap();
      toast.success("Notifications cleared");
    } catch (e) {
      toast.error(e?.message || "Could not clear notifications");
    }
  };

  return (
    <li className="nav-item me-2" ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="btn p-0 d-flex align-items-center justify-content-center position-relative"
        style={{ width: 38, height: 38, borderRadius: "50%", background: MAROON, color: "#fff" }}
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        title="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className="d-flex align-items-center justify-content-center"
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 4px",
              borderRadius: 999,
              background: "#C62828",
              border: "2px solid #fff",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {/* Past 99 the exact number stops meaning anything and starts
                breaking the badge's width. */}
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="card shadow"
          style={{
            position: "absolute",
            top: 46,
            right: 0,
            width: 360,
            maxWidth: "90vw",
            zIndex: 1050,
          }}
        >
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <span className="fw-bold" style={{ fontSize: 14 }}>Notifications</span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-danger"
              onClick={handleClearAll}
              disabled={isClearing || items.length === 0}
              title="Clear all"
            >
              <Trash2 size={14} className="me-1" />
              Clear all
            </button>
          </div>

          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {isFetching && items.length === 0 ? (
              <p className="text-muted small text-center my-4">Loading...</p>
            ) : unread.length === 0 ? (
              <p className="text-muted small text-center my-4">No notifications yet.</p>
            ) : (
              unread.map((item) => {
                const isCritical = item.priority === "CRITICAL";
                const hasTarget = Boolean(detailPathFor(item));
                return (
                  <button
                    key={item.recordGuid}
                    type="button"
                    className="btn text-start w-100 border-bottom px-3 py-2 d-flex gap-2"
                    onClick={() => handleOpenItem(item)}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: isCritical ? "#C62828" : MAROON,
                        color: "#fff",
                      }}
                    >
                      {isCritical ? <AlertTriangle size={15} /> : <Bell size={15} />}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span className="d-block fw-bold text-truncate" style={{ fontSize: 13 }}>
                        {item.title}
                      </span>
                      <span className="d-block text-muted" style={{ fontSize: 12, whiteSpace: "normal" }}>
                        {item.message}
                      </span>
                      <span className="d-block text-muted" style={{ fontSize: 11 }}>
                        {relativeTime(item.createdDate)}
                        {/* Says up front whether clicking goes anywhere - some
                            modules that raise notifications have no web screen
                            to land on, and a click that only marks read should
                            not look like a broken link. */}
                        {hasTarget && (
                          <>
                            {" · "}
                            <ExternalLink size={10} style={{ verticalAlign: "-1px" }} /> open
                          </>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
